/**
 * /settings/entities — multi-entity admin (Phase A).
 *
 * Lists every entity the operator can see. Admins see ALL entities and
 * can create new ones; non-admins see only entities they have access to
 * (read-only).
 *
 * Each entity row shows: code · legal name · EIN · base currency · state
 * of formation · is_active. Clicking opens an inline panel for editing
 * basic fields + managing user access (admin only).
 *
 * Per-state tax registrations + parent_entity_id are exposed in the
 * inline panel for now — fancier UX (sub-table for state tax IDs, parent
 * picker autocomplete) are scope creep we can layer in later.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Autocomplete,
  Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, MenuItem, Stack, Switch, TextField,
} from "@mui/material";

import { api } from "@/lib/api";
import {
  entityKeys,
  entitiesListQueryOptions,
  entityUsersQueryOptions,
  meQueryOptions,
  usersListQueryOptions,
} from "@/lib/queries";
import { useToast } from "@/components/Toaster";
import { FiscalYearChange } from "@/components/FiscalYearChange";
import type { Entity, EntityCreate, User, UserEntityAccess } from "@/lib/types";

export const Route = createFileRoute("/_auth/settings/entities")({
  component: EntitiesPage,
});


function EntitiesPage() {
  const qc = useQueryClient();
  const toast = useToast();

  const meQuery = useQuery(meQueryOptions());
  const entitiesQuery = useQuery(entitiesListQueryOptions(true));
  const isAdmin = meQuery.data?.role === "admin";

  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Entity | null>(null);

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Entities</h1>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {entitiesQuery.data?.length ?? 0} entit
            {entitiesQuery.data?.length === 1 ? "y" : "ies"}
            {!isAdmin && " (you can see)"}
          </span>
          <div className="flex-1" />
          {isAdmin && (
            <button
              onClick={() => { setEditTarget(null); setFormOpen(true); }}
              className="text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-1.5 font-medium"
            >
              + New entity
            </button>
          )}
        </div>
      </div>

      {/* Entity list — one card per entity, expandable to show users */}
      <div className="flex-1 min-h-0 overflow-auto px-6 py-4 space-y-3">
        {(entitiesQuery.data ?? []).map((e) => (
          <EntityCard
            key={e.id}
            entity={e}
            isAdmin={isAdmin}
            onEdit={() => { setEditTarget(e); setFormOpen(true); }}
          />
        ))}
        {(entitiesQuery.data ?? []).length === 0 && (
          <div className="bg-white border border-slate-200 rounded-md p-8 text-center text-slate-500 dark:bg-gray-900 dark:border-gray-800 dark:text-slate-400">
            No entities yet.
            {isAdmin && " Click + New entity to add the first one."}
          </div>
        )}
      </div>

      <EntityFormDialog
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditTarget(null); }}
        target={editTarget}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: entityKeys.all });
          qc.invalidateQueries({ queryKey: ["auth", "me", "entities"] });
          toast.success(editTarget ? "Entity updated" : "Entity created");
        }}
      />
    </div>
  );
}


function EntityFormDialog({
  open, onClose, target, onSaved,
}: {
  open: boolean;
  onClose: () => void;
  target: Entity | null;
  onSaved: () => void;
}) {
  const isEdit = target !== null;
  const toast = useToast();

  const [code, setCode] = useState(target?.code ?? "");
  const [legalName, setLegalName] = useState(target?.legal_name ?? "");
  const [displayName, setDisplayName] = useState(target?.display_name ?? "");
  const [ein, setEin] = useState(target?.ein ?? "");
  const [stateOfFormation, setStateOfFormation] = useState(target?.state_of_formation ?? "");
  const [fyMonth, setFyMonth] = useState<number>(target?.fiscal_year_start_month ?? 1);
  const [baseCurrency, setBaseCurrency] = useState(target?.base_currency_code ?? "USD");
  const [primaryColor, setPrimaryColor] = useState(target?.primary_color ?? "#0066cc");
  const [isActive, setIsActive] = useState(target?.is_active ?? true);
  const [isConsolidatingParent, setIsConsolidatingParent] = useState(
    target?.is_consolidating_parent ?? false,
  );

  // Re-hydrate when target switches
  if (open && target && code === "" && target.code !== "") {
    setCode(target.code);
    setLegalName(target.legal_name);
    setDisplayName(target.display_name);
    setEin(target.ein ?? "");
    setStateOfFormation(target.state_of_formation ?? "");
    setFyMonth(target.fiscal_year_start_month);
    setBaseCurrency(target.base_currency_code);
    setPrimaryColor(target.primary_color ?? "#0066cc");
    setIsActive(target.is_active);
    setIsConsolidatingParent(target.is_consolidating_parent);
  }

  const save = useMutation({
    mutationFn: async () => {
      if (isEdit && target) {
        return api(`/api/entities/${target.id}`, {
          method: "PATCH",
          body: {
            legal_name: legalName.trim(),
            display_name: displayName.trim(),
            ein: ein.trim() || null,
            state_of_formation: stateOfFormation.trim() || null,
            // The start month of a company with books changes only through
            // Change fiscal year (preview → apply), below.
            base_currency_code: baseCurrency,
            primary_color: primaryColor || null,
            is_active: isActive,
            is_consolidating_parent: isConsolidatingParent,
          },
        });
      }
      const payload: EntityCreate = {
        code: code.trim().toUpperCase(),
        legal_name: legalName.trim(),
        display_name: displayName.trim() || legalName.trim().slice(0, 80),
        ein: ein.trim() || null,
        state_of_formation: stateOfFormation.trim() || null,
        fiscal_year_start_month: fyMonth,
        base_currency_code: baseCurrency,
        primary_color: primaryColor || null,
        is_consolidating_parent: isConsolidatingParent,
      };
      return api("/api/entities", { method: "POST", body: payload });
    },
    onSuccess: () => {
      onSaved();
      // Reset state on close so opening a new dialog doesn't show stale data
      setCode(""); setLegalName(""); setDisplayName(""); setEin("");
      setStateOfFormation(""); setFyMonth(1); setBaseCurrency("USD");
      setPrimaryColor("#0066cc"); setIsActive(true);
      setIsConsolidatingParent(false);
      onClose();
    },
    onError: (err: Error) => toast.error(`Save failed: ${err.message}`),
  });

  const submit = () => {
    if (!isEdit && !code.trim()) {
      toast.error("Code is required");
      return;
    }
    if (!legalName.trim()) {
      toast.error("Legal name is required");
      return;
    }
    save.mutate();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? `Edit ${target?.display_name}` : "New entity"}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Code *"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              size="small"
              disabled={isEdit}
              helperText={isEdit ? "Code is immutable" : "Short identifier (e.g. CCNV)"}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Display name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              size="small"
              helperText="Shown in UI / switcher"
              sx={{ flex: 2 }}
            />
          </Stack>

          <TextField
            label="Legal name *"
            value={legalName}
            onChange={(e) => setLegalName(e.target.value)}
            size="small"
            helperText="Full legal name on tax forms"
          />

          <Stack direction="row" spacing={2}>
            <TextField
              label="EIN"
              value={ein}
              onChange={(e) => setEin(e.target.value)}
              size="small"
              helperText="Federal tax ID — XX-XXXXXXX"
              sx={{ flex: 1 }}
            />
            <TextField
              label="State of formation"
              value={stateOfFormation}
              onChange={(e) => setStateOfFormation(e.target.value.toUpperCase().slice(0, 2))}
              size="small"
              inputProps={{ maxLength: 2 }}
              helperText="2-letter (e.g. NV)"
              sx={{ flex: 1 }}
            />
            <TextField
              label="Currency"
              value={baseCurrency}
              onChange={(e) => setBaseCurrency(e.target.value.toUpperCase().slice(0, 3))}
              size="small"
              inputProps={{ maxLength: 3 }}
              sx={{ flex: 1 }}
            />
          </Stack>

          {isEdit && target && (
            <FiscalYearChange entityId={target.id} currentMonth={target.fiscal_year_start_month} />
          )}

          <Stack direction="row" spacing={2} alignItems="center">
            {!isEdit && (
              <TextField
                label="FY start month"
                type="number"
                value={fyMonth}
                onChange={(e) => setFyMonth(Math.max(1, Math.min(12, Number(e.target.value))))}
                size="small"
                inputProps={{ min: 1, max: 12 }}
                helperText="1-12"
                sx={{ flex: 1 }}
              />
            )}
            <TextField
              label="Brand color"
              type="color"
              value={primaryColor}
              onChange={(e) => setPrimaryColor(e.target.value)}
              size="small"
              helperText="UI tint per entity"
              sx={{ flex: 1 }}
            />
            <FormControlLabel
              control={
                <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              }
              label="Active"
            />
          </Stack>

          <FormControlLabel
            control={
              <Switch
                checked={isConsolidatingParent}
                onChange={(e) => setIsConsolidatingParent(e.target.checked)}
              />
            }
            label="Consolidating parent (holdco)"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={save.isPending}
        >
          {save.isPending ? "Saving…" : isEdit ? "Save" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


// ──────────────────────────────────────────────────────────────
// Entity card — header + expandable users panel
// ──────────────────────────────────────────────────────────────
function EntityCard({
  entity, isAdmin, onEdit,
}: {
  entity: Entity;
  isAdmin: boolean;
  onEdit: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun",
                  "Jul","Aug","Sep","Oct","Nov","Dec"];

  return (
    <div
      className="bg-white border rounded-md overflow-hidden dark:bg-gray-900"
      style={{
        borderColor: entity.primary_color || "rgb(226, 232, 240)",
        borderLeftWidth: entity.primary_color ? 4 : 1,
      }}
    >
      {/* Header row */}
      <div className="px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => setExpanded((v) => !v)}
          className="text-slate-400 hover:text-slate-700 text-xs font-mono w-4 dark:text-slate-500"
          title={expanded ? "Collapse" : "Show users with access"}
        >
          {expanded ? "▼" : "▶"}
        </button>
        {entity.primary_color && (
          <span
            className="inline-block w-3 h-3 rounded-full"
            style={{ background: entity.primary_color }}
          />
        )}
        <div className="flex-1 min-w-0 flex items-baseline gap-3">
          <span className="font-mono text-sm font-semibold text-slate-900 dark:text-slate-100">
            {entity.code}
          </span>
          <span className="text-sm text-slate-700 dark:text-slate-300">{entity.legal_name}</span>
          {entity.is_consolidating_parent && (
            <span className="text-[10px] text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded">
              consolidating parent
            </span>
          )}
          {!entity.is_active && (
            <span className="text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded dark:bg-gray-800 dark:text-slate-400">
              inactive
            </span>
          )}
        </div>
        <div className="text-xs text-slate-500 flex items-center gap-4 dark:text-slate-400">
          <span title="EIN">
            {entity.ein || <span className="text-slate-400 dark:text-slate-500">no EIN</span>}
          </span>
          <span title="State of formation">
            {entity.state_of_formation || <span className="text-slate-400 dark:text-slate-500">—</span>}
          </span>
          <span title="Base currency">{entity.base_currency_code}</span>
          <span title="Fiscal year start">
            FY: {MONTHS[entity.fiscal_year_start_month - 1]}
          </span>
        </div>
        {isAdmin && (
          <button
            onClick={onEdit}
            className="text-xs text-blue-600 hover:text-blue-800 ml-2"
          >
            Edit
          </button>
        )}
      </div>

      {/* Expanded users panel */}
      {expanded && (
        <EntityUsersPanel entityId={entity.id} entityCode={entity.code} isAdmin={isAdmin} />
      )}
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
// Users-with-access panel (expanded row content)
// ──────────────────────────────────────────────────────────────
function EntityUsersPanel({
  entityId, entityCode, isAdmin,
}: {
  entityId: string;
  entityCode: string;
  isAdmin: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const usersQuery = useQuery(entityUsersQueryOptions(entityId));
  const allUsersQuery = useQuery({ ...usersListQueryOptions(), enabled: isAdmin });
  const [granting, setGranting] = useState(false);

  const revoke = useMutation({
    mutationFn: async (userId: string) =>
      api(`/api/entities/${entityId}/users/${userId}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["entities", "users", entityId] });
      toast.success("Access revoked");
    },
    onError: (err: Error) => toast.error(`Revoke failed: ${err.message}`),
  });

  const access = usersQuery.data ?? [];

  return (
    <div className="bg-slate-50 border-t border-slate-200 px-4 py-3 dark:bg-gray-950 dark:border-gray-800">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide dark:text-slate-300">
          Users with access
          {access.length > 0 && (
            <span className="ml-2 text-slate-500 font-normal normal-case dark:text-slate-400">
              ({access.length})
            </span>
          )}
        </div>
        {isAdmin && !granting && (
          <button
            onClick={() => setGranting(true)}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            + Grant access
          </button>
        )}
      </div>

      {usersQuery.isLoading ? (
        <div className="text-xs text-slate-500 dark:text-slate-400">Loading…</div>
      ) : access.length === 0 && !granting ? (
        <div className="text-xs text-slate-500 dark:text-slate-400">
          No users have access yet.
        </div>
      ) : (
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
            <tr>
              <th className="text-left py-1 font-medium">User</th>
              <th className="text-left py-1 font-medium">Role</th>
              <th className="text-left py-1 font-medium">Default?</th>
              <th className="text-left py-1 font-medium">Granted</th>
              <th className="text-right py-1"></th>
            </tr>
          </thead>
          <tbody>
            {access.map((a: UserEntityAccess) => (
              <tr key={a.user_id} className="border-t border-slate-200 dark:border-gray-800">
                <td className="py-1.5">
                  <div className="text-slate-800 dark:text-slate-200">{a.user_full_name ?? "?"}</div>
                  <div className="text-[11px] text-slate-500 dark:text-slate-400">{a.user_email ?? ""}</div>
                </td>
                <td className="py-1.5">
                  <span className="bg-slate-200 px-1.5 py-0.5 rounded text-[10px] dark:bg-gray-700">
                    {a.role}
                  </span>
                </td>
                <td className="py-1.5">
                  {a.is_default ? (
                    <span className="text-emerald-700">✓ default</span>
                  ) : (
                    <span className="text-slate-400 dark:text-slate-500">—</span>
                  )}
                </td>
                <td className="py-1.5 text-slate-500 dark:text-slate-400">
                  {new Date(a.granted_at).toLocaleDateString()}
                </td>
                <td className="py-1.5 text-right">
                  {isAdmin && !a.is_default && (
                    <button
                      onClick={() => {
                        if (confirm(`Revoke ${a.user_email}'s access to ${entityCode}?`)) {
                          revoke.mutate(a.user_id);
                        }
                      }}
                      className="text-red-600 hover:text-red-800"
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {granting && isAdmin && (
        <GrantAccessForm
          entityId={entityId}
          existingUserIds={access.map((a) => a.user_id)}
          allUsers={allUsersQuery.data ?? []}
          onDone={() => {
            setGranting(false);
            qc.invalidateQueries({ queryKey: ["entities", "users", entityId] });
          }}
        />
      )}
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
// Inline grant-access form (shown when + Grant access clicked)
// ──────────────────────────────────────────────────────────────
function GrantAccessForm({
  entityId, existingUserIds, allUsers, onDone,
}: {
  entityId: string;
  existingUserIds: string[];
  allUsers: User[];
  onDone: () => void;
}) {
  const toast = useToast();
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState("viewer");
  const [isDefault, setIsDefault] = useState(false);

  const eligible = allUsers.filter(
    (u) => u.is_active && !existingUserIds.includes(u.id),
  );

  const grant = useMutation({
    mutationFn: async () =>
      api(`/api/entities/${entityId}/users`, {
        method: "POST",
        body: { user_id: user!.id, role, is_default: isDefault },
      }),
    onSuccess: () => {
      toast.success("Access granted");
      onDone();
    },
    onError: (err: Error) => toast.error(`Grant failed: ${err.message}`),
  });

  return (
    <div className="mt-3 pt-3 border-t border-slate-200 bg-white rounded p-3 dark:bg-gray-900 dark:border-gray-800">
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Autocomplete
          size="small"
          sx={{ flex: 2, minWidth: 240 }}
          value={user}
          onChange={(_, v) => setUser(v)}
          options={eligible}
          getOptionLabel={(u) => `${u.full_name || u.email}`}
          renderInput={(p) => (
            <TextField {...p} label="User" placeholder="Pick a user…" />
          )}
        />
        <TextField
          select size="small" label="Role" value={role}
          onChange={(e) => setRole(e.target.value)}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="admin">admin</MenuItem>
          <MenuItem value="accountant">accountant</MenuItem>
          <MenuItem value="ap_clerk">ap_clerk</MenuItem>
          <MenuItem value="ar_clerk">ar_clerk</MenuItem>
          <MenuItem value="viewer">viewer</MenuItem>
        </TextField>
        <FormControlLabel
          control={
            <Switch
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
              size="small"
            />
          }
          label={<span className="text-xs">Make default</span>}
          sx={{ mr: 0 }}
        />
        <Button size="small" onClick={onDone} disabled={grant.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained" size="small"
          disabled={!user || grant.isPending}
          onClick={() => grant.mutate()}
        >
          {grant.isPending ? "Granting…" : "Grant"}
        </Button>
      </Stack>
      {eligible.length === 0 && (
        <div className="text-[11px] text-slate-500 mt-2 dark:text-slate-400">
          All active users already have access to this entity.
        </div>
      )}
    </div>
  );
}
